import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppLayout,
  SideNavigation,
  SideNavigationProps,
  BreadcrumbGroup,
  HelpPanel,
  Badge,
  Select
} from "@cloudscape-design/components";

import TopNavigationPanel from "./TopNavigationPanel";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useHelpPanel } from "../../contexts/HelpPanelContext";
import AppConfig from "configs/AppConfig";
import { getBasePath, openInNewTab } from "../../utils/Utils";
import organizationService from "services/organizationService";
import { OrganizationsListObject } from "types/organizationTypes";
import { useCluster } from "contexts/ClusterContext";
import { OptionDefinition } from "@cloudscape-design/components/internal/components/option/interfaces";



const AppLayoutWrapper: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeHref, setActiveHref] = useState(location.pathname);
  const [isNavigationOpen, setIsNavigationOpen] = useState(true);
  const { breadcrumbs } = useBreadcrumbs();
  const { isHelpPanelOpen, setIsHelpPanelOpen, helpPanelContent } = useHelpPanel();
  
  // Organization
  const [organization, setOrganization] = useState<OrganizationsListObject | null>(null);

  // Cluster
  const { clusters, loadingClusters, loadingClustersError, fetchClusters, selectedCluster, setSelectedCluster } = useCluster();


  // fetch my organization data
  const fetchOrganizationData = async ()=>{
    try {
      const response = await organizationService.getOrganizations();
      setOrganization(response[0]);
      
    } catch (error) {
      console.error("Failed to fetch organization: ", error);
    }
  }

  useEffect(()=>{
    fetchOrganizationData();
    fetchClusters();
  },[])



  const allNavigationItems: SideNavigationProps.Item[] = [
    {
      type: "section-group",
      title: "Deployments",
      items: [
        {
          type: "link",
          text: "Dashboard",
          href: "/dashboard",
        },
        {
          type: "link",
          text: "Dedicated Clusters",
          href: "/dedicated-clusters",
        },
        { 
          type: "link",
          text: "Extensions",
          href: `/dedicated-clusters/${selectedCluster?.value}/extensions`
        },
        {
          type: "link",
          text: "SIEM Integration",
          href: "#activity-logs",
          info: <Badge>Coming Soon</Badge>
        },
        // deactivated for now, as we removed shared deployments
        // {
        //   type: "link",
        //   text: "Shared Deployments",
        //   href: "/shared-deployments",
        // }
      ],
    },
    {
      type: "divider"
    },
    {
      type: "section-group",
      title: "Organization",
      items: [
        {
          type: "link",
          text: "My Organization",
          href: `/organizations/${organization?.id}`,
        },
        {
          type: "link",
          text: "Settings",
          href: `/organizations/${organization?.id}/settings`,
        },
        {
          type: "link",
          text: "Domains",
          href: `/organizations/${organization?.id}/domains`,
        },
        {
          type: "link",
          text: "Invite Members",
          href: `/organizations/${organization?.id}/invite`,
        },
      ],
    },
    {
      type: "divider"
    },
    {
      type: "section-group",
      title: "Support",
      items: [
        {
          type: "link",
          text: "Contact",
          href: "/contact",
        },
        {
          type: "link",
          text: "Documentation",
          href: AppConfig.ASWAR_DOCUMENTATION_URL ?? "#",
          external: true
        },
      ],
    },
  ];



  // Prepare cluster options list
  const clusterOptionsList: OptionDefinition[] = clusters.map(
    (cluster) => ({
      label: cluster.name,
      value: cluster.id,
    })
  );



  
  const navigationItems = selectedCluster
  ? allNavigationItems
  : allNavigationItems.filter((item) => !(item.type === "section-group" && item.title === "Clusters"));


  const defaultClusterOption: OptionDefinition = {
    label: "Select a cluster",
    value: "",
  };

  const sideNavigation = (
    <SideNavigation
      activeHref={activeHref}
      header={{
        href: "/",
        text: AppConfig.APP_NAME
      }}
      onFollow={(event) => {
        if (!event.detail.external) {
          event.preventDefault();
          setActiveHref(getBasePath(event.detail.href));
          navigate(event.detail.href);
        }
      }}
      items={navigationItems}
      itemsControl={
        <Select
          selectedOption={selectedCluster ?? defaultClusterOption}
          options={clusterOptionsList}
          placeholder="Select a cluster"
          loadingText="Loading clusters..."
          statusType={
            loadingClusters
              ? "loading"
              : loadingClustersError
              ? "error"
              : "finished"
          }
          errorText={loadingClustersError ?? undefined}
          empty={
            <div style={{ padding: "8px" }}>
              No clusters available.
            </div>
          }
          onChange={({ detail }) => {
            const cluster = detail.selectedOption;

            setSelectedCluster(cluster);

            const isExtensionsPage = location.pathname.endsWith("/extensions");

            if (cluster?.value && isExtensionsPage) {
              const href = `/dedicated-clusters/${cluster.value}/extensions`;
              setActiveHref(href);
              navigate(href);
            }
          }}
        />
      }
    />
  );

  useEffect(() => {
    setActiveHref(getBasePath(location.pathname));
  }, [location]);

  const defaultBreadcrumbs = (() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const items = [{ text: AppConfig.APP_NAME, href: "/" }];
    pathSegments.reduce((acc, segment) => {
      const href = `${acc}/${segment}`;
      items.push({
        text: segment.charAt(0).toUpperCase() + segment.slice(1),
        href,
      });
      return href;
    }, "");

    return items;
  })();


  useEffect(() => {
    setActiveHref(location.pathname);
  }, [location]);


  return (
    <div className="layout-wrapper">
      <div className="top-nav">
        <TopNavigationPanel />
      </div>
      <AppLayout
        navigationOpen={isNavigationOpen}
        onNavigationChange={({ detail }) => setIsNavigationOpen(detail.open)}
        navigation={sideNavigation}
        content={<Outlet />}
        toolsOpen={isHelpPanelOpen}
        onToolsChange={({ detail }) => setIsHelpPanelOpen(detail.open)}
        tools={
          <HelpPanel header={<h2>{helpPanelContent.header}</h2>}>
            {helpPanelContent.content}
          </HelpPanel>
        }
        toolsHide={false}
        breadcrumbs={
          <BreadcrumbGroup
            items={breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs}
            ariaLabel="Breadcrumbs"
            onFollow={(event) => {
              if (!event.detail.external) {
                event.preventDefault();
                navigate(event.detail.href);
              }
            }}
          />
        }
      />
      <footer className="custom-footer">
        {/* Footer left section (empty) */}
        <div></div>

        {/* Footer center section */}
        <div 
          className="center-footer-text ellipsis-effect clickable"
          onClick={()=>{
            openInNewTab(AppConfig.CLOUD_COMMIT_WEBSITE_URL);
          }}
        >
          <strong>Copyright © {new Date().getFullYear()} Cloud Commit, Inc</strong>
        </div>

        {/* Footer right section */}
        <div className="ellipsis-effect software-version"><strong>v{AppConfig.SOFTWARE_VERSION}</strong></div>
      </footer>
    </div>
  );
};

export default AppLayoutWrapper;
